import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { ToolAppPage } from '@/pages/ToolAppPage';
import { ToolPage } from '@/pages/ToolPage';
import { type AppSummary, loadAppByKey, loadAppKeyOfFlow } from '@/lib/app-access';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const Loading = () => <p role="status" className="px-6 py-12 text-sm text-domino-inkSoft">Chargement de l’application…</p>;

/**
 * F5-0: one address per app. The app of a Forge tool opens as its tool; a read
 * app opens its surfaces. An unknown or inaccessible key falls to the read page,
 * which says so.
 */
export const AppRoute = () => {
  const { toolAppKey, recordId } = useParams<{ toolAppKey: string; recordId: string }>();
  const { search, hash } = useLocation();
  const [state, setState] = useState<{ key: string; app: AppSummary | null } | null>(null);

  useEffect(() => {
    let active = true;
    if (!toolAppKey) return;
    loadAppByKey(toolAppKey)
      .then((app) => { if (active) setState({ key: toolAppKey, app }); })
      .catch(() => { if (active) setState({ key: toolAppKey, app: null }); });
    return () => { active = false; };
  }, [toolAppKey]);

  if (!state || state.key !== toolAppKey) return <Loading />;
  if (state.app?.kind === 'flow' && state.app.flowId) {
    const params = new URLSearchParams(search);
    const legacyRecordId = params.get('record');
    if (!recordId && legacyRecordId) {
      params.delete('record');
      const query = params.toString();
      return <Navigate to={`/apps/${encodeURIComponent(state.app.key)}/o/${encodeURIComponent(legacyRecordId)}${query ? `?${query}` : ''}${hash}`} replace />;
    }
    return <ToolPage key={state.app.id} flowId={state.app.flowId} app={state.app} recordId={recordId} />;
  }
  if (recordId) return <div className="px-6 py-12 text-sm text-domino-inkSoft">
    <p>Cette page est introuvable ou vous n’y avez pas accès.</p>
    <Link to="/tools" className="mt-3 inline-block text-domino-aubergine underline">Revenir aux applications</Link>
  </div>;
  return <ToolAppPage />;
};

/** Old tool address: a Forge tool moves to its app's address, keeping the run and other parameters. */
export const ToolRoute = () => {
  const { toolKey } = useParams<{ toolKey: string }>();
  const { search, hash } = useLocation();
  const isFlowId = Boolean(toolKey && UUID_PATTERN.test(toolKey));
  const [appKey, setAppKey] = useState<{ flowId: string; key: string | null } | null>(null);

  useEffect(() => {
    let active = true;
    if (!toolKey || !isFlowId) return;
    void loadAppKeyOfFlow(toolKey).then((key) => { if (active) setAppKey({ flowId: toolKey, key }); });
    return () => { active = false; };
  }, [toolKey, isFlowId]);

  if (!isFlowId) return <ToolPage />;
  if (!appKey || appKey.flowId !== toolKey) return <Loading />;
  if (appKey.key) return <Navigate to={`/apps/${encodeURIComponent(appKey.key)}${search}${hash}`} replace />;
  return <ToolPage />;
};
